import { format, parseISO } from 'date-fns';
import React from 'react';
import type { ProgramMilestone, ProviderReferralPayable } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { TooltippedText } from '../../../components/tooltipped-text';
import { StatusCodes } from '../../../constants/workflows';

interface ReferralMilestoneItemProps {
  milestone: ProgramMilestone;
  payable?: ProviderReferralPayable | null;
}

const CONTAINER_BASE = 'tw:flex tw:flex-col tw:items-center tw:text-center tw:gap-1';
const CIRCLE_BASE = 'tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-full';
const TITLE_CLASS = 'tw:text-xs tw:font-normal tw:text-zinc-500';
const AMOUNT_BASE = 'tw:text-base tw:font-bold';

function buildTooltipContent(
  milestone: ProgramMilestone,
  payable?: ProviderReferralPayable | null
): React.ReactNode {
  const lines: string[] = [milestone.description ?? ''].filter(Boolean);

  if (payable) {
    try {
      const d = payable.payment_at ? parseISO(payable.payment_at) : parseISO(payable.earned_at);
      lines.push(`Date: ${format(d, 'MMM d, yyyy')}`);
      lines.push(`Status: ${payable.current_status?.title}`);
    } catch {
      if (payable.payment_at ?? payable.earned_at) {
        lines.push(`Date: ${payable.payment_at ?? payable.earned_at}`);
      }
    }
    if (payable.payment_method) {
      lines.push(`Method: ${payable.payment_method}`);
    }
    if (payable.payment_reference) {
      lines.push(`Reference: ${payable.payment_reference}`);
    }
  }

  return (
    <span className="tw:block tw:whitespace-pre-line">{lines.filter(Boolean).join('\n')}</span>
  );
}

export function ReferralMilestoneItem({ milestone, payable }: ReferralMilestoneItemProps) {
  const { formatCurrency } = useCurrencyFormatter({ postfix: '', placeholder: '--' });

  const isPending = payable && payable.current_status?.code === StatusCodes.PAYABLE;
  const isPaid = payable && payable.current_status?.code === StatusCodes.PAID;

  // Amount priority:
  // 1) If there's a payable and payment_amount > 0, use payment_amount
  // 2) Else if there's a payable, use its amount
  // 3) Else use the milestone value
  const amountSource = payable
    ? Number(payable.payment_amount) > 0
      ? Number(payable.payment_amount)
      : Number(payable.amount ?? milestone.value)
    : Number(milestone.value);
  const amountDisplay = formatCurrency(Math.round(amountSource));
  const tooltipContent = buildTooltipContent(milestone, payable);

  const getContainerClass = () => {
    if (isPaid) return CONTAINER_BASE;
    if (isPending) return CONTAINER_BASE;
    return `${CONTAINER_BASE} tw:text-zinc-500`;
  };

  const getCircleClass = () => {
    if (isPaid)
      return `${CIRCLE_BASE} tw:bg-stoplight-success/30 tw:border tw:border-stoplight-success/90`;
    if (isPending) return `${CIRCLE_BASE} tw:bg-amber-100 tw:border tw:border-amber-400/80`;
    return `${CIRCLE_BASE} tw:border tw:border-dashed tw:border-zinc-300 tw:bg-zinc-50`;
  };

  const getIcon = () => {
    if (isPaid) return <i className="fas fa-check tw:text-stoplight-success" aria-hidden />;
    if (isPending) return <i className="fas fa-hourglass-half tw:text-amber-600" aria-hidden />;
    return <i className="fas fa-minus tw:text-zinc-400" aria-hidden />;
  };

  const getAmountClass = () => {
    if (isPaid) return `${AMOUNT_BASE} tw:text-stoplight-success`;
    if (isPending) return `${AMOUNT_BASE} tw:text-amber-600`;
    return `${AMOUNT_BASE} tw:text-zinc-400`;
  };

  return (
    <TooltippedText tooltipContent={tooltipContent} spanClassName="tw:inline-flex">
      <span className={getContainerClass()}>
        <div className={getCircleClass()}>{getIcon()}</div>
        <span className={TITLE_CLASS}>{milestone.title}</span>
        <span className={getAmountClass()}>{amountDisplay}</span>
      </span>
    </TooltippedText>
  );
}
